/**
 * Context Keywords and Device Type Matching
 *
 * Shared utilities for matching user input keywords to device types,
 * used by both "Relevant to your message" suggestions and thinking messages.
 */

import {
	isMotion,
	isDoorWindow,
	isTRV,
	isTemperatureHumidity,
	isFlood,
	isSmoke,
} from '../../../helpers/interface_helpers';

/**
 * Device type regex patterns for matching device types
 * Used by both suggestion logic and thinking message context detection
 */
export const deviceTypePatterns: Record<string, RegExp> = {
	lighting: /^(SHSW|SHDM|SHRGBW|SNDM|SPDM|S3DM|SNSW)/,
	dimmer: /^(SHDM|SNDM|SPDM|S3DM)/,
	covers: /^(SHSW-2|SPSH|S3SH)/,
	energy: /^(SHEM|SPEM|S3EM|SHPM)/,
	plug: /^(SHPLG|SNPL|SPPL|S3PL|SHPM)/,
	switch: /^(SHSW|SNSW|SPSW|S3SW|S4SW)/,
	button: /^(SHB|SNB|S3BT|SBBT)/,
};

/**
 * Keyword to device type matcher mapping for smart suggestions
 * Maps user input keywords to functions that check if a device type matches
 */
export const contextKeywords: Record<string, (type: string) => boolean> = {
	light: (type) => deviceTypePatterns.lighting.test(type),
	lamp: (type) => deviceTypePatterns.lighting.test(type),
	dimmer: (type) => deviceTypePatterns.dimmer.test(type),
	temperature: isTemperatureHumidity,
	humidity: isTemperatureHumidity,
	thermostat: isTRV,
	heating: isTRV,
	motion: isMotion,
	movement: isMotion,
	door: isDoorWindow,
	window: isDoorWindow,
	flood: isFlood,
	water: isFlood,
	leak: isFlood,
	smoke: isSmoke,
	fire: isSmoke,
	plug: (type) => deviceTypePatterns.plug.test(type),
	outlet: (type) => deviceTypePatterns.plug.test(type),
	switch: (type) => deviceTypePatterns.switch.test(type),
	relay: (type) => deviceTypePatterns.switch.test(type),
	cover: (type) => deviceTypePatterns.covers.test(type),
	shutter: (type) => deviceTypePatterns.covers.test(type),
	blind: (type) => deviceTypePatterns.covers.test(type),
	button: (type) => deviceTypePatterns.button.test(type),
};

/**
 * Keywords that map to context categories for thinking messages
 * Used for keyword-based context detection in user input
 */
export const thinkingContextKeywords: Record<string, string[]> = {
	lighting: ['light', 'lamp', 'dim', 'bright', 'color', 'rgb', 'bulb', 'led', 'glow', 'illuminate'],
	climate: [
		'temperature',
		'thermostat',
		'heat',
		'cool',
		'warm',
		'cold',
		'hvac',
		'trv',
		'climate',
		'humid',
	],
	security: [
		'motion',
		'sensor',
		'detect',
		'door',
		'window',
		'alarm',
		'security',
		'flood',
		'smoke',
		'alert',
	],
	covers: ['cover', 'shutter', 'blind', 'curtain', 'roller', 'shade', 'open', 'close', 'tilt'],
	scheduling: [
		'schedule',
		'time',
		'morning',
		'evening',
		'night',
		'sunset',
		'sunrise',
		'when',
		'daily',
		'weekly',
	],
	energy: ['energy', 'power', 'watt', 'consumption', 'meter', 'usage', 'save', 'efficient'],
	scene: ['scene', 'automation', 'scenario', 'routine', 'workflow', 'together', 'all'],
};

/**
 * Device info interface for suggestion matching
 */
interface DeviceInfo {
	id: string | number;
	name: string;
	type?: string;
}

/**
 * Get device suggestions based on user input keywords
 *
 * Analyzes the user's input for keywords and returns matching devices
 * for the "Relevant to your message" section.
 *
 * @param input - The user's input text
 * @param allDevices - Object map of all available devices
 * @returns Array of matching devices (max 5, deduplicated)
 */
export function getSuggestionsForInput(
	input: string,
	allDevices: Record<string, DeviceInfo>,
): Array<{ id: string; name: string; type: string }> {
	const lowerInput = input.toLowerCase();
	const suggestions: Array<{ id: string; name: string; type: string }> = [];

	for (const [keyword, matcher] of Object.entries(contextKeywords)) {
		if (lowerInput.includes(keyword)) {
			const matching = Object.values(allDevices).filter((d) => d.type && matcher(d.type));
			suggestions.push(
				...matching.map((d) => ({ id: String(d.id), name: d.name, type: d.type || '' })),
			);
		}
	}

	// Remove duplicates and limit to 5
	const uniqueIds = new Set<string>();
	return suggestions
		.filter((s) => {
			if (uniqueIds.has(s.id)) return false;
			uniqueIds.add(s.id);
			return true;
		})
		.slice(0, 5);
}

/**
 * Device context info for language-agnostic context detection
 * Uses device metadata instead of relying on keyword matching
 */
export interface DeviceContextInfo {
	category?: string;
	appliance_type?: number;
	relay_usage?: string;
	type?: string;
	isMotion?: boolean;
	isDoorWindow?: boolean;
	isTRV?: boolean;
	isFlood?: boolean;
	isSmoke?: boolean;
}

/**
 * Detect context categories from device metadata (language-agnostic)
 * This allows non-English users to get contextual messages when they mention devices
 */
export function detectContextFromDevices(devices: DeviceContextInfo[]): string[] {
	const detectedContexts = new Set<string>();

	for (const device of devices) {
		// Check category (most reliable)
		if (device.category === 'light') {
			detectedContexts.add('lighting');
		}
		if (device.category === 'roller') {
			detectedContexts.add('covers');
		}
		if (device.category === 'thermostat') {
			detectedContexts.add('climate');
		}
		if (device.category === 'sensor') {
			// Sensor type detection via boolean flags
			if (device.isMotion || device.isDoorWindow) {
				detectedContexts.add('security');
			}
			if (device.isFlood || device.isSmoke) {
				detectedContexts.add('security');
			}
		}
		if (device.category === 'emeter' || device.category === 'power_meter') {
			detectedContexts.add('energy');
		}

		// Check appliance_type (user-assigned usage)
		if (device.appliance_type === 2) {
			// light
			detectedContexts.add('lighting');
		}
		if (device.appliance_type === 4) {
			// heating
			detectedContexts.add('climate');
		}
		if (device.appliance_type === 15) {
			// roller
			detectedContexts.add('covers');
		}

		// Check relay_usage
		if (device.relay_usage === 'light') {
			detectedContexts.add('lighting');
		}
		if (device.relay_usage === 'heating') {
			detectedContexts.add('climate');
		}

		// Check TRV flag
		if (device.isTRV) {
			detectedContexts.add('climate');
		}

		// Fallback: check device type regex patterns
		if (device.type) {
			for (const [context, pattern] of Object.entries(deviceTypePatterns)) {
				// Map pattern keys to context categories
				const contextMap: Record<string, string> = {
					lighting: 'lighting',
					dimmer: 'lighting',
					covers: 'covers',
					energy: 'energy',
				};
				if (pattern.test(device.type) && contextMap[context]) {
					detectedContexts.add(contextMap[context]);
				}
			}
		}
	}

	return Array.from(detectedContexts);
}

/**
 * Detect which context categories are relevant based on user input keywords
 */
export function detectThinkingContext(input: string): string[] {
	const lowerInput = input.toLowerCase();
	const detectedContexts: string[] = [];

	for (const [context, keywords] of Object.entries(thinkingContextKeywords)) {
		if (keywords.some((keyword) => lowerInput.includes(keyword))) {
			detectedContexts.push(context);
		}
	}

	return detectedContexts;
}
